import { type HighlightSegment, segmentsFromSpans } from "../../../../../components/UIToolkit/molecules/HighlightedText";
import type { AirtableFacilityOption } from "./airtableImportTypes";

/**
 * One candidate that survived the filter, with the runs that matched already located.
 *
 * The segments are computed here rather than in the row renderer so the matcher and the highlighter can
 * never disagree about what matched — a row emphasizing characters the filter did not accept is a small
 * lie about why it is on screen.
 */
export type AirtableFacilityMatch = {
	option: AirtableFacilityOption;
	/** The facility name, split into matched / unmatched runs — `null` when the record links no facility. */
	nameSegments: HighlightSegment[] | null;
	/** The network name, likewise — `null` when the facility has no network. */
	networkSegments: HighlightSegment[] | null;
	/** The intake record's title, likewise — `null` when it has none. */
	kickoffSegments: HighlightSegment[] | null;
};

/** Every occurrence of `needle` in `haystack`, as the half-open ranges `segmentsFromSpans` consumes. */
function occurrences(haystack: string, needle: string): [number, number][] {
	if (needle.length === 0) return [];
	/**
	 * Case folding must not change the string's LENGTH, or every offset past the folded character is
	 * wrong and the highlight lands on the wrong words. A handful of characters do change it
	 * (`"İ".toLowerCase()` is two code units), so a fold that resizes drops the highlighting and renders
	 * the row plain — the row still matches, it just does not claim to know where. Same trade as
	 * `AirtableSourceEvidence`: a wrong highlight is worse than none.
	 */
	const foldedHaystack = haystack.toLowerCase();
	const foldedNeedle = needle.toLowerCase();
	if (foldedHaystack.length !== haystack.length) return [];

	const ranges: [number, number][] = [];
	let cursor = foldedHaystack.indexOf(foldedNeedle);
	while (cursor !== -1) {
		ranges.push([cursor, cursor + foldedNeedle.length]);
		cursor = foldedHaystack.indexOf(foldedNeedle, cursor + foldedNeedle.length);
	}
	return ranges;
}

/** The searchable identifiers of a record: facility, network, and the intake record's own title. */
function searchableTexts(option: AirtableFacilityOption): (string | null)[] {
	return [option.facilityName, option.network, option.kickoffRecord];
}

/** Case-insensitive substring on any identifier. The predicate the emphasized runs are derived from. */
function matches(option: AirtableFacilityOption, folded: string): boolean {
	return searchableTexts(option).some((text) => text?.toLowerCase().includes(folded) === true);
}

/** `text` split into runs matching `query`, or `null` when there is no text. */
function segmentsFor(text: string | null, query: string): HighlightSegment[] | null {
	return text === null ? null : segmentsFromSpans(text, occurrences(text, query));
}

/**
 * Narrow the loaded intake records to the ones the operator's text names — the whole of the picker's
 * search, run in memory on every keystroke.
 *
 * Three properties it is built around, all of them things a server round trip per keystroke got wrong:
 *
 * 1. **Every identifier is searched** — facility, network, and the intake record's title. Two rows for
 *    one facility differ only in their title, which is exactly the pair an operator disambiguates by
 *    typing, and a network name finds every facility in it.
 * 2. **Order is the server's, always.** The list is never re-sorted by relevance — a list that reorders
 *    under the cursor moves the row somebody was about to click, which is worse than one that shrinks.
 * 3. **An empty query is the whole list**, not an empty screen. Everything is already loaded, so there
 *    is nothing to gain by making the operator type before showing them anything.
 */
export function filterAirtableFacilities(options: AirtableFacilityOption[], query: string): AirtableFacilityMatch[] {
	const trimmed = query.trim();
	const folded = trimmed.toLowerCase();

	return options.flatMap((option) => {
		if (trimmed.length > 0 && !matches(option, folded)) return [];
		return [
			{
				option,
				nameSegments: segmentsFor(option.facilityName, trimmed),
				networkSegments: segmentsFor(option.network, trimmed),
				kickoffSegments: segmentsFor(option.kickoffRecord, trimmed),
			},
		];
	});
}

/**
 * The loaded listing plus whatever an Airtable-side search found beyond it, each record once.
 *
 * Loaded records keep their place and order; search hits the listing never loaded are appended after
 * them. Appending rather than interleaving keeps the "order is the server's, never re-sorted" rule above:
 * a row the operator can already see does not move when the search lands.
 */
export function mergeAirtableFacilities(loaded: AirtableFacilityOption[], searched: AirtableFacilityOption[]): AirtableFacilityOption[] {
	const loadedIds = new Set(loaded.map((option) => option.recordId));
	return [...loaded, ...searched.filter((option) => !loadedIds.has(option.recordId))];
}

/**
 * Facility names held by more than one record. For those records — and for a record that links no
 * facility — the intake record's own title is the only thing that tells them apart, so the picker shows it.
 */
export function ambiguousFacilityNames(options: AirtableFacilityOption[]): ReadonlySet<string> {
	const seen = new Set<string>();
	const repeated = new Set<string>();
	for (const { facilityName } of options) {
		if (facilityName === null) continue;
		if (seen.has(facilityName)) repeated.add(facilityName);
		seen.add(facilityName);
	}
	return repeated;
}
